import { useEffect, useMemo, useState } from "react";
import { CarIcon, DeleteIcon, EditIcon, EyeIcon, ProfileIcon } from "../../utils/icon.utils";
import Table from "../../ui/Table/Table";
import { GroupBtn } from "../../ui/GroupBtn";
import { extractValues, filterValues } from "../../utils/stockList.utils";
import {
  useDeleteCustomerAttachmentMutation,
  useExportMoneyReceiptsCsv,
  useGetAllCustomerAttachmentQuery
} from "../../utils/customerAttachment.utils";
import { onConfirm } from "../../utils/sweetAlert.utils";
import Loading from "../../components/Loading";
import PageLayout from "../../layouts/PageLayout";
import { useNavigate } from "react-router-dom";
import { objectToQueryString } from "../../utils/object.utils";
import MoneyReceiptToolBar from "./MoneyReceiptToolBar";
import MoneyReceiptExportModal from "./MoneyReceiptExportModal";
import { printDate, tableDate } from "../../utils/date.utils";
import { getJson } from "../../utils/json.utils";
import { arrayMap, arrayTextPrint } from "../../utils/array.utils";
import { currency } from "../../utils/number.utils";
import { Badge } from "reactstrap";
import Button from "../../ui/Button";
import { useQueryParams } from "../../utils/queryParams.utils";
import { onError } from "../../utils/toastify.utils";

export default function MoneyReceiptListPage({
  isBreadCrumbs = true,
  style = { hasCardBg: true, pageLayoutClassName: "pt-4" },
  customer: existCustomer,
  isCustomer = false
}) {
  const { fullPath: redirectTo, searchParams } = useQueryParams();
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [isSearched, setIsSearched] = useState(false);
  const [isExportOpen, setIsExportOpen] = useState(false);
  const { exporting: isExporting, exportCsv } = useExportMoneyReceiptsCsv();
  const pageSize = 10;

  // is exist customer information?
  const isExistCustomer = !!existCustomer?.profile?.id;

  // filter data
  const { setFilter, getFilter } = filterValues();
  const filteredValues = getFilter();
  const { values } = extractValues(filteredValues);
  const initParams = useMemo(() => {
    return {
      page: 1,
      size: pageSize,
      type_id: 2,
      // initiall set filter data if have,
      customer_id: existCustomer?.profile?.id ?? "",
      searchCount: 0,
      ...values
    };
  }, [existCustomer?.profile?.id, values]);

  const [params, setParams] = useState(initParams);

  useEffect(() => {
    if (existCustomer?.profile?.id) {
      setParams((prev) => ({ ...prev, customer_id: existCustomer?.profile?.id }));
    }
  }, [existCustomer?.profile?.id]);

  useEffect(() => {
    setParams((prev) => ({ ...prev, page: currentPage }));
  }, [currentPage]);

  const { isLoading: loading, data } = useGetAllCustomerAttachmentQuery({
    query: `?${objectToQueryString(params)}&${searchParams}`,
    ...(isCustomer
      ? {
          options: {
            enabled: !!params?.customer_id
          }
        }
      : {})
  });

  const onSearch = (values) => {
    setFilter(null);
    setParams((prev) => ({
      ...prev,
      page: 1,
      size: pageSize,
      ...values
    }));
    setIsSearched(true);
  };

  const handleSearchClear = () => {
    setParams((prev) => ({
      ...prev,
      page: 1,
      size: pageSize,
      searchCount: params.searchCount + 1,
      caid: ""
    }));
    setIsSearched(false);
  };

  const onFilterClear = () => {
    setFilter(null);
    setParams({ ...initParams, page: 1, size: pageSize });
  };

  const { loading: deleting, deleteCustomerAttachment } = useDeleteCustomerAttachmentMutation([
    "deleteCustomerAttachment"
  ]);

  const totalPage = data?.last_page === 1 && data?.total === 0 ? 0 : data?.last_page;

  const breadCrumbs = isBreadCrumbs
    ? [
        {
          name: "Pages",
          link: "/pages"
        },
        {
          name: "Money Receipt"
        }
      ]
    : [];

  const customerThead = !isExistCustomer ? ["Customer"] : [];

  const thead = ["#", "Car", ...customerThead, "MR Date", "Date", "Action"];

  function parseAttachement(attachment) {
    return attachment ? getJson(attachment) : {};
  }

  const MrDate = ({ data }) => {
    const attachment = parseAttachement(data?.attachment);
    const mrDate = attachment?.qoutDate ? printDate(attachment.qoutDate) : "-";
    return mrDate;
  };

  const CustomerInfo = ({ data }) => {
    const attachment = parseAttachement(data?.attachment);

    const customer = {
      name: attachment?.customer?.name ?? "",
      mobile: attachment?.customer?.mobile ?? "",
      address: attachment?.customer?.address ?? ""
    };

    const customerArray = Object.values(customer);

    const customer_id = data?.customer?.customer_id;
    return (
      <div>
        {arrayTextPrint(customerArray)}
        <br />
        {!!customer_id && (
          <Button onClick={() => navigate(`/customer/${customer_id}/view`)} icon={ProfileIcon} />
        )}
      </div>
    );
  };

  const CarInfo = ({ data }) => {
    const attachment = parseAttachement(data?.attachment);
    const car = attachment?.car ?? {};
    const carArray = [car?.chassis ?? "", car?.name ?? "", car?.year ?? "", car?.color ?? ""];

    if (data?.is_multi) {
      return (
        <div>
          <Badge color="info">{car?.length}</Badge> Multi Cars
          <br /> {!isNaN(attachment?.amount?.total) ? currency(attachment.amount.total) : ""}
        </div>
      );
    }

    const carId = car?.sku ?? "";
    return (
      <div>
        {arrayTextPrint(carArray)}
        <br /> {car?.total ? currency(car.total) : ""}
        <br />
        {!!carId && (
          <Button
            color="info"
            onClick={() => navigate(`/stock-list/${carId}/view`)}
            icon={CarIcon}
          />
        )}
      </div>
    );
  };

  const tbody =
    arrayMap(data?.data, (item) => {
      const date = item?.created_at ? tableDate(item.created_at) : "-";
      return {
        "#": item?.caid ?? "-",
        Customer: <CustomerInfo data={item} />,
        Car: <CarInfo data={item} />,
        "MR Date": <MrDate data={item} />,
        Date: date,
        ...item
      };
    }) || [];

  // table action button group
  const ActionComponent = ({ data }) => {
    const handleEdit = (item) => {
      navigate(`/money-receipt-list/${item?.caid}/edit`, {
        state: { data: existCustomer, redirectTo }
      });
    };

    const handleDelete = async (item) => {
      const { isConfirmed } = await onConfirm();
      if (isConfirmed) {
        await deleteCustomerAttachment(item?.id);
      }
    };

    function handleView(data) {
      navigate(`/money-receipt-list/${data?.caid}/view`, { state: { existCustomer } });
    }

    const btns = [
      {
        icon: DeleteIcon,
        onClick: () => handleDelete(data),
        color: "danger"
      },
      {
        icon: EditIcon,
        onClick: () => handleEdit(data),
        color: "primary"
      },
      {
        icon: EyeIcon,
        onClick: () => handleView(data),
        color: "info"
      }
    ];
    return <GroupBtn btns={btns} justify="start" loading={false} />;
  };

  const isLoading = loading || deleting;

  const handleAdd = () => {
    navigate("/money-receipt-list/add", {
      state: { data: existCustomer, redirectTo }
    });
  };

  const handleExport = async (params, suffix) => {
    setIsExportOpen(false);
    const result = await exportCsv(params, `money_receipts_${suffix}.csv`);
    if (result?.status) {
      onError(result?.message ?? "Failed to export money receipts.");
    }
  };

  return (
    <PageLayout
      title="Money Receipt"
      subTitle={`Total: ${data?.total ?? 0}`}
      breadCrumbs={breadCrumbs}
      style={style}>
      <MoneyReceiptToolBar
        handleAdd={handleAdd}
        filteredValues={filteredValues}
        onFilterClear={onFilterClear}
        onSearch={onSearch}
        isSearched={isSearched}
        handleSearchClear={handleSearchClear}
        onExport={() => setIsExportOpen(true)}
        isExporting={isExporting}
      />

      <MoneyReceiptExportModal
        isOpen={isExportOpen}
        toggle={() => setIsExportOpen(false)}
        onExport={handleExport}
        isExporting={isExporting}
        defaultCustomerId={existCustomer?.profile?.id ?? ""}
        defaultCaid={params?.caid ?? ""}
      />

      <Table
        thead={thead}
        tbody={tbody}
        action={ActionComponent}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        totalPage={totalPage}
      />
      {<Loading loading={isLoading} />}
    </PageLayout>
  );
}
